<template>
  <div class="tw-space-y-3">
    <Text class="tw-flex tw-items-center tw-gap-2">
      {{ t('sidebar_status.wan_connection') }}

      <template v-if="wanWifi">
        <Icon name="wifi" class="tw-size-6 tw-fill-white/89" />
        <Text tag="span" class="tw-font-black">[{{ wanProvider || '-' }}]</Text>
      </template>

      <template v-else-if="wanCellular">
        <Icon :name="SignalCellular3Svg" class="tw-size-6 tw-fill-white/89" />
        <Text tag="span" class="tw-font-black">[cellular]</Text>
      </template>

      <template v-else>
        <Icon :name="SitemapOutlineSvg" class="tw-size-6 tw-fill-white/89" />
        <Text tag="span" class="tw-font-black">[ethernet]</Text>
      </template>

      <template v-if="wanOnline === false">
        <Spinner size="small" :title="t('sidebar_status.connecting')" />
        <Text tag="span" size="small" class="tw-opacity-80">{{ t('sidebar_status.connecting') }}</Text>
      </template>
    </Text>

    <div v-if="wanOnline" class="tw-flex tw-items-start tw-justify-between tw-gap-2">
      <div class="tw-space-y-1">
        <Text>
          {{ t('sidebar_status.station_is') }}
          <Text tag="span" class="tw-font-black tw-text-state-succ">{{ t('sidebar_status.online') }}</Text>
        </Text>

        <template v-if="hasSpeedtestMetrics">
          <Text tag="p" class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-opacity-90">
            <Text tag="span" class="tw-flex tw-items-center tw-gap-1">
              <Icon name="arrow-down" class="tw-size-2 tw-fill-white/89" />
              <Text tag="span" class="tw-font-black">{{ wanDownText }}</Text>
            </Text>

            <Text tag="span" class="tw-flex tw-items-center tw-gap-1">
              <Icon name="arrow-up" class="tw-size-2 tw-fill-white/89" />
              <Text tag="span" class="tw-font-black">{{ wanUpText }}</Text>
            </Text>

            <Text tag="span" class="tw-text-purple/25">|</Text>

            <Text tag="span" class="tw-flex tw-items-center tw-gap-1">
              <Text tag="span" class="tw-text-white/89">{{ t('sidebar_status.ping') }}</Text>
              <Text tag="span" class="tw-font-black">{{ wanPingText }}</Text>
            </Text>

            <template v-if="showJitter">
              <Text tag="span" class="tw-text-purple/25">|</Text>

              <Text tag="span" class="tw-flex tw-items-center tw-gap-1">
                <Text tag="span" class="tw-text-white/89">{{ t('sidebar_status.jitter') }}</Text>
                <Text tag="span" class="tw-font-black">{{ wanJitterText }}</Text>
              </Text>
            </template>

            <Text tag="span" class="tw-text-purple/25">|</Text>

            <div>
              <Text v-if="isLoading" tag="span">Running…</Text>

              <Text v-else tag="span">
                {{ t('sidebar_status.speedtest') }}
                <Text tag="span" class="tw-font-black">{{ wanSpeedtestAgeText }}</Text>
              </Text>
            </div>

            <CircularButton
              :icon="RefreshSvg"
              size="small"
              color="transparentWhite"
              class="tw-ml-1 tw-flex-shrink-0"
              :loading="isLoading"
              :aria-label="t('sidebar_status.refresh_wan')"
              @click="onRefreshClick"
            />
          </Text>
        </template>

        <template v-else>
          <Button size="small" color="transparentWhite" outlined :loading="isLoading" @click="onRefreshClick">
            {{ t('sidebar_status.run_speedtest') }}
          </Button>
        </template>
      </div>
    </div>

    <Text v-else>
      {{ t('sidebar_status.station_is') }}
      <Text tag="span" class="tw-font-black tw-text-state-err">{{ t('sidebar_status.offline') }}</Text>
    </Text>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button, CircularButton, Icon, Spinner, Text } from '@slideslive/fuse-kit/vue';

import RefreshSvg from '@/assets/mdi/refresh.svg';
import SignalCellular3Svg from '@/assets/mdi/signal-cellular-3.svg';
import SitemapOutlineSvg from '@/assets/mdi/sitemap-outline.svg';
import useStateStore from '@/stores/state';
import { formatAge, formatBps } from '@/utils/sidebar_status';

const emit = defineEmits(['refresh']);

const { t } = useI18n();

const { state, speedtestRunning } = storeToRefs(useStateStore());

const wanState = computed(() => state.value?.data?.wan || {});
const wanWifi = computed(() => wanState.value?.type === 'wifi');
const wanCellular = computed(() => wanState.value?.type === 'cellular');
const wanProvider = computed(() => wanState.value?.provider);
const wanOnline = computed(() => (typeof wanState.value?.online === 'boolean' ? wanState.value.online : null));
const wanSpeedtest = computed(() => wanState.value?.speedtest || null);

const wanDownText = computed(() =>
  wanSpeedtest.value?.downlink_bps ? formatBps(wanSpeedtest.value.downlink_bps) : '-',
);
const wanUpText = computed(() => (wanSpeedtest.value?.uplink_bps ? formatBps(wanSpeedtest.value.uplink_bps) : '-'));
const wanPingText = computed(() =>
  typeof wanSpeedtest.value?.ping === 'number' ? `${wanSpeedtest.value.ping} ms` : '-',
);
const wanJitterText = computed(() =>
  typeof wanSpeedtest.value?.jitter === 'number' ? `${wanSpeedtest.value.jitter} ms` : '-',
);
const wanSpeedtestAgeText = computed(() => {
  const ageS = wanSpeedtest.value?.age_s;
  return typeof ageS === 'number' ? formatAge(ageS) : '-';
});

const isLoading = computed(() => Boolean(speedtestRunning.value));
const hasSpeedtestMetrics = computed(
  () => wanDownText.value !== '-' || wanUpText.value !== '-' || wanPingText.value !== '-',
);
const showJitter = computed(() => wanJitterText.value !== '-');

const onRefreshClick = () => {
  if (speedtestRunning.value) return;
  emit('refresh');
};
</script>
